import loadable from '@loadable/component';
import type React from 'react';
import type { RouteObject } from 'react-router';
import { useRoutes } from 'react-router-dom';

import { AnalyticsTab, GamePromotionTab, GettingStartedTab, IntegrationTab, PlatformsTab, Route } from 'src/constants';
import type { AnalyticsProps } from 'src/pages/analytics/analytics';
import type { GamePromotionProps } from 'src/pages/game-promotion/game-promotion';
import type { IntegrationProps } from 'src/pages/integration/integration';
import type { IntegrationGuideProps } from 'src/pages/integration-guide/integration-guide';
import type { IntegrationStatusProps } from 'src/pages/integration-status/integration-status';
import type { OverviewProps } from 'src/pages/overview/overview';
import type { PlatformsProps } from 'src/pages/platforms/platforms';

const Overview = loadable<OverviewProps>(() => import('src/pages/overview/overview'));
const Analytics = loadable<AnalyticsProps>(() => import('src/pages/analytics/analytics'));
const ChurnRate = loadable(() => import('src/pages/churn-rate/churn-rate'));
const Platforms = loadable<PlatformsProps>(() => import('src/pages/platforms/platforms'));
const IntegrationStatus = loadable<IntegrationStatusProps>(
  () => import('src/pages/integration-status/integration-status'),
);
const Integration = loadable<IntegrationProps>(() => import('src/pages/integration/integration'));
const GamePromotion = loadable<GamePromotionProps>(() => import('src/pages/game-promotion/game-promotion'));
const CostOptimization = loadable(() => import('src/pages/cost-optimization/cost-optimization'));
const PriorityImpact = loadable(() => import('src/pages/priority-impact/priority-impact'));
const GameCollection = loadable(() => import('src/pages/game-collection/game-collection'));
const Recommendations = loadable(() => import('src/pages/recommendations/recommendations'));
const IntegrationGuide = loadable<IntegrationGuideProps>(() => import('src/pages/integration-guide/integration-guide'));
const SignIn = loadable(() => import('src/pages/sign-in/sign-in'));
const Calculator = loadable(() => import('src/pages/calculator/calculator'));
const DemoStand = loadable(() => import('src/pages/demo-stand/demo-stand'));
const NotAllowed = loadable(() => import('src/pages/not-allowed/not-allowed'));
const NotFound = loadable(() => import('src/pages/not-found/not-found'));

export const getRoutes = (): RouteObject[] => [
  {
    path: Route.OVERVIEW,
    element: <Overview />,
    caseSensitive: true,
  },
  ...Object.values(GettingStartedTab).map((tab) => ({
    path: `${Route.GETTING_STARTED}/${tab}`,
    element: <Overview tab={tab} />,
    caseSensitive: true,
  })),
  {
    path: Route.ANALYTICS,
    element: <Analytics />,
    caseSensitive: true,
  },
  ...Object.values(AnalyticsTab).map((tab) => ({
    path: `${Route.ANALYTICS}/:brandId/${tab}`,
    element: <Analytics tab={tab} />,
    caseSensitive: true,
  })),
  {
    path: Route.CHURN_RATE,
    element: <ChurnRate />,
    caseSensitive: true,
  },
  {
    path: `${Route.CHURN_RATE}/:brandId`,
    element: <ChurnRate />,
    caseSensitive: true,
  },
  {
    path: Route.PLATFORMS,
    element: <Platforms />,
    caseSensitive: true,
  },
  ...Object.values(PlatformsTab).map((tab) => ({
    path: `${Route.PLATFORMS}/:platformId/${tab}`,
    element: <Platforms tab={tab} />,
    caseSensitive: true,
  })),
  {
    path: Route.INTEGRATION_STATUS,
    element: <IntegrationStatus />,
    caseSensitive: true,
  },
  ...Object.values(GettingStartedTab).map((tab) => ({
    path: `${Route.INTEGRATION_STATUS}/:platformId/${tab}`,
    element: <IntegrationStatus tab={tab} />,
    caseSensitive: true,
  })),
  ...Object.values(IntegrationTab).map((tab) => ({
    path: `${Route.INTEGRATION}/${tab}`,
    element: <Integration tab={tab} />,
    caseSensitive: true,
  })),
  {
    path: Route.GAME_PROMOTION,
    element: <GamePromotion />,
    caseSensitive: true,
  },
  ...Object.values(GamePromotionTab).map((tab) => ({
    path: `${Route.GAME_PROMOTION}/:brandId/${tab}`,
    element: <GamePromotion tab={tab} />,
    caseSensitive: true,
  })),
  {
    path: Route.COST_OPTIMIZATION,
    element: <CostOptimization />,
    caseSensitive: true,
  },
  {
    path: `${Route.COST_OPTIMIZATION}/:brandId`,
    element: <CostOptimization />,
    caseSensitive: true,
  },
  {
    path: Route.PRIORITY_IMPACT,
    element: <PriorityImpact />,
    caseSensitive: true,
  },
  {
    path: `${Route.PRIORITY_IMPACT}/:brandId`,
    element: <PriorityImpact />,
    caseSensitive: true,
  },
  {
    path: Route.GAME_COLLECTION,
    element: <GameCollection />,
    caseSensitive: true,
  },
  {
    path: `${Route.GAME_COLLECTION}/:brandId`,
    element: <GameCollection />,
    caseSensitive: true,
  },
  {
    path: Route.RECOMMENDATIONS,
    element: <Recommendations />,
    caseSensitive: true,
  },
  {
    path: `${Route.RECOMMENDATIONS}/:brandId`,
    element: <Recommendations />,
    caseSensitive: true,
  },
  {
    path: Route.INTEGRATION_GUIDE,
    element: <IntegrationGuide />,
    caseSensitive: true,
  },
  ...Object.values(GettingStartedTab).map((tab) => ({
    path: `${Route.INTEGRATION_GUIDE}/${tab}`,
    element: <IntegrationGuide tab={tab} />,
    caseSensitive: true,
  })),
  {
    path: Route.SIGN_IN,
    element: <SignIn />,
    caseSensitive: true,
  },
  {
    path: Route.CALCULATOR,
    element: <Calculator />,
    caseSensitive: true,
  },
  {
    path: Route.DEMO_STAND,
    element: <DemoStand />,
    caseSensitive: true,
  },
  {
    path: Route.NOT_ALLOWED,
    element: <NotAllowed />,
    caseSensitive: true,
  },
  {
    path: Route.NOT_FOUND,
    element: <NotFound />,
    caseSensitive: true,
  },
];

export const Router: React.FC = () => useRoutes(getRoutes());
